/**
 * features/finance/CashBook.tsx
 * ------------------------------------------------------------------
 * MONEY THROUGH THE BANK THAT NO INVOICE AND NO VOUCHER COVERS.
 *
 * Bank charges. Interest received. A refund from a supplier. A donation.
 * The municipal direct debit. Every one is real money moving through a
 * real account, and until now the only way to record one was a general
 * journal — which means an accounts clerk could not enter a bank charge
 * without knowing which side of a double entry the bank goes on. So the
 * charges went unrecorded until somebody senior had an hour, and the
 * bank reconciliation never came out.
 *
 * ONE SIDE IS ALWAYS THE BANK. That single constraint is what makes this
 * safe for somebody who does not think in debits and credits: choose
 * money in or money out, name the account it relates to, and the
 * direction follows. A clerk never chooses a side.
 *
 * ENTERED IN A BATCH, POSTED ONE JOURNAL PER LINE. A clerk works down a
 * bank statement and enters a dozen at a time, so the capture screen is a
 * grid. But the bank reconciliation matches a statement line to a ledger
 * entry, and one journal carrying twelve unrelated movements is a journal
 * that can never be ticked off against anything.
 *
 * A PARTLY-POSTED BATCH SAYS SO. Twelve journals are twelve writes, and
 * the ninth can fail. Reporting "posted" over a batch that half-landed is
 * how a books-agree message ends up on a set of books that do not.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  BookOpen, Plus, Trash2, AlertTriangle, CheckCircle2, ArrowDownLeft, ArrowUpRight,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useGlData } from '@/features/finance/useGlData';
import { postJournal } from '@/features/finance/glWrite';
import {
  CASH_ENTRY_HELP, emptyEntry, entryIssues, isBlank,
  cashEntryLines, batchTotals, validateEntry,
  type CashEntryDraft, type CashEntryKind,
} from '@/lib/cashBookEntry';
import type { GlAccount, GlJournal } from '@/lib/gl';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);

/** A bank or cash account, which is where one side of every entry lands. */
const isBankish = (a: GlAccount) =>
  a.type === 'asset' && /bank|cash/i.test(`${a.code} ${a.name}`);

export default function CashBook() {
  const { userData } = useAuth();
  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);
  const [tab, setTab] = useState<'capture' | 'book'>('capture');

  const banks = useMemo(() => gl.accounts.filter(isBankish), [gl.accounts]);
  const [bankCode, setBankCode] = useState('');
  const bank = bankCode || banks[0]?.code || '';

  if (gl.loading) return <div className="p-6 flex justify-center"><Spinner /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={BookOpen} tone="navy"
        title="Cash Book"
        subtitle="Bank charges, interest, donations — money moving that no invoice or voucher covers"
        actions={(
          <select value={fy} onChange={(e) => setFy(e.target.value)}
            className={glassFieldClass('max-w-[130px] text-sm')}>
            {[0, 1, 2].map((n) => {
              const y = new Date().getFullYear() - n;
              return <option key={y} value={`FY${y}`}>{`FY${y}`}</option>;
            })}
          </select>
        )}
      />

      {banks.length === 0 ? (
        <SoftCard>
          <p className="text-sm text-gray-500 text-center py-6">
            No bank or cash account exists in the chart of accounts yet. One side of every
            cash-book entry is the bank, so there is nothing to post against.
          </p>
        </SoftCard>
      ) : (
        <>
          <div className="flex gap-1 mb-4">
            {([['capture', 'Enter from a statement'], ['book', 'The book']] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  tab === k ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}>{label}</button>
            ))}
          </div>

          {tab === 'capture' ? (
            <Capture
              accounts={gl.accounts} banks={banks} bank={bank} onBank={setBankCode}
              actor={{ uid: userData?.uid, name: userData?.displayName, email: userData?.email }}
            />
          ) : (
            <TheBook journals={gl.journals} accounts={gl.accounts} banks={banks}
              bank={bank} onBank={setBankCode} />
          )}
        </>
      )}
    </div>
  );
}

/* -------------------------------- capture -------------------------------- */

function Capture({
  accounts, banks, bank, onBank, actor,
}: {
  accounts: GlAccount[];
  banks: GlAccount[];
  bank: string;
  onBank: (c: string) => void;
  actor: { uid?: string; name?: string; email?: string };
}) {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState<CashEntryDraft[]>(
    () => [emptyEntry('payment', bank, today())],
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ posted: number; failed: { row: string; why: string }[] } | null>(null);

  const typeOf = (code: string) => accounts.find((a) => a.code === code)?.type;
  const issues = entryIssues(rows, typeOf);
  const totals = batchTotals(rows);
  const live = rows.filter((r) => !isBlank(r));

  /* The contra list deliberately excludes the bank accounts. Moving money
     between two of the school's own accounts is a TRANSFER; recorded here
     it would show the school earning income every time it did so. */
  const contras = useMemo(
    () => accounts.filter((a) => !isBankish(a)).sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  function setRow(key: string, patch: Partial<CashEntryDraft>) {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addRow(kind: CashEntryKind) {
    setRows((rs) => [...rs, emptyEntry(kind, bank, date)]);
  }

  /**
   * Post each line as its own journal, and report honestly.
   *
   * Sequential and independent: one bad line must not take the other
   * eleven down with it, and a line that failed has to be nameable so it
   * can be fixed rather than re-keyed from scratch.
   */
  async function post() {
    setBusy(true);
    setResult(null);
    const failed: { row: string; why: string }[] = [];
    const survivors: CashEntryDraft[] = [];
    let posted = 0;

    for (const r of live) {
      try {
        await postJournal({
          type: 'cash_book',
          sourceId: `cb_${r.key}_${Date.now()}`,
          date: r.date,
          narration: r.description,
          lines: cashEntryLines(r),
          actor,
        });
        posted += 1;
      } catch (e) {
        failed.push({ row: r.description || r.reference || '(unnamed)', why: (e as Error).message });
        survivors.push(r);
      }
    }

    /* Keep exactly what did not post, so a retry is a correction rather
       than re-typing a statement page. */
    setRows(survivors.length ? survivors : [emptyEntry('payment', bank, date)]);
    setResult({ posted, failed });
    setBusy(false);
  }

  return (
    <>
      {result && (
        <SoftCard className={`mb-4 border-l-4 ${result.failed.length ? 'border-amber-400' : 'border-emerald-400'}`}>
          <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
            {result.failed.length
              ? <AlertTriangle size={15} className="text-amber-600" />
              : <CheckCircle2 size={15} className="text-emerald-600" />}
            {result.posted} posted{result.failed.length ? `, ${result.failed.length} refused and left in the grid` : ''}.
          </p>
          {result.failed.map((f) => (
            <p key={f.row} className="text-[13px] text-gray-600 dark:text-gray-300 mt-0.5">
              · <strong>{f.row}</strong> — {f.why}
            </p>
          ))}
        </SoftCard>
      )}

      <SoftCard pad="p-0">
        <PanelHeader
          icon={BookOpen} tone="navy" title="Enter from a statement"
          subtitle="One journal per line, so each can be ticked off against the statement"
          right={(
            <div className="flex items-center gap-2">
              <select value={bank} onChange={(e) => onBank(e.target.value)}
                className={glassFieldClass('max-w-[220px] text-xs')}>
                {banks.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
              </select>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className={glassFieldClass('max-w-[150px] text-xs')} />
            </div>
          )}
        />

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold w-28">In or out</th>
                <th className="px-2 font-semibold w-32">Date</th>
                <th className="px-2 font-semibold">Account it relates to</th>
                <th className="px-2 font-semibold">What it was for</th>
                <th className="px-2 font-semibold w-32">Reference</th>
                <th className="px-2 font-semibold w-28 text-right">Amount</th>
                <th className="px-2 pr-4 w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const problem = validateEntry(r, typeOf);
                return (
                  <tr key={r.key} className="border-b border-gray-50 dark:border-gray-800 align-top">
                    <td className="py-1.5 pl-4 pr-2">
                      <select value={r.kind}
                        onChange={(e) => setRow(r.key, { kind: e.target.value as CashEntryKind })}
                        className={glassFieldClass('text-xs')}>
                        <option value="receipt">Money in</option>
                        <option value="payment">Money out</option>
                      </select>
                    </td>
                    <td className="px-2">
                      <input type="date" value={r.date}
                        onChange={(e) => setRow(r.key, { date: e.target.value })}
                        className={glassFieldClass('text-xs')} />
                    </td>
                    <td className="px-2">
                      <select value={r.contraCode}
                        onChange={(e) => setRow(r.key, { contraCode: e.target.value })}
                        className={glassFieldClass('text-xs')}>
                        <option value="">— choose —</option>
                        {contras.map((a) => (
                          <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                        ))}
                      </select>
                      {problem && (
                        <span className="block text-[11px] text-amber-600 mt-0.5">{problem}</span>
                      )}
                    </td>
                    <td className="px-2">
                      <input value={r.description}
                        onChange={(e) => setRow(r.key, { description: e.target.value })}
                        className={glassFieldClass('text-xs')} placeholder="Monthly service fee" />
                    </td>
                    <td className="px-2">
                      <input value={r.reference}
                        onChange={(e) => setRow(r.key, { reference: e.target.value })}
                        className={glassFieldClass('text-xs')} placeholder="On the statement" />
                    </td>
                    <td className="px-2">
                      <input type="number" step="0.01" value={r.amount || ''}
                        onChange={(e) => setRow(r.key, { amount: Number(e.target.value) })}
                        className={glassFieldClass('text-xs text-right')} />
                    </td>
                    <td className="px-2 pr-4 pt-2">
                      {rows.length > 1 && (
                        <button type="button" className="text-gray-400 hover:text-red-600"
                          onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={btnGhost} onClick={() => addRow('payment')}>
            <Plus size={14} /> Money out
          </button>
          <button type="button" className={btnGhost} onClick={() => addRow('receipt')}>
            <Plus size={14} /> Money in
          </button>
          <p className="text-[12px] text-gray-500 ml-2">{CASH_ENTRY_HELP.payment}</p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
          <div className="text-sm tabular-nums">
            <span className="text-emerald-700 dark:text-emerald-400">
              in {money(totals.receipts)}
            </span>
            <span className="text-gray-400"> · </span>
            <span className="text-red-700 dark:text-red-400">out {money(totals.payments)}</span>
            <span className="text-gray-400"> · </span>
            <span className="font-bold text-gray-900 dark:text-white">net {money(totals.net)}</span>
            <span className="text-gray-500"> over {totals.rows} line{totals.rows === 1 ? '' : 's'}</span>
          </div>
          <button type="button" className={btnPrimary}
            disabled={busy || totals.rows === 0 || issues.length > 0}
            onClick={post}>
            {busy ? 'Posting…' : `Post ${totals.rows} entr${totals.rows === 1 ? 'y' : 'ies'}`}
          </button>
        </div>
      </SoftCard>
    </>
  );
}

/* --------------------------------- book --------------------------------- */

function TheBook({
  journals, accounts, banks, bank, onBank,
}: {
  journals: GlJournal[];
  accounts: GlAccount[];
  banks: GlAccount[];
  bank: string;
  onBank: (c: string) => void;
}) {
  const nameOf = (code: string) => accounts.find((a) => a.code === code)?.name ?? code;

  /**
   * Every movement through this bank account, from any source.
   *
   * NOT just cash-book entries. The question "what has gone through the
   * bank" is answered by the account, and an invoice receipt moves the
   * bank exactly as a bank charge does — showing only what was typed here
   * would produce a book that reconciles against nothing.
   */
  const rows = useMemo(() => {
    const out: {
      id: string; date: string; ref: string; narration: string;
      dr: number; cr: number; contra: string;
    }[] = [];

    for (const j of journals) {
      const mine = (j.lines ?? []).filter((l) => l.accountCode === bank);
      if (mine.length === 0) continue;
      const others = (j.lines ?? []).filter((l) => l.accountCode !== bank);
      for (const l of mine) {
        out.push({
          id: `${j.id}-${l.accountCode}-${l.dr}-${l.cr}`,
          date: j.date,
          ref: j.journalNo ?? j.id,
          narration: l.narration || j.narration || '',
          dr: Number(l.dr) || 0,
          cr: Number(l.cr) || 0,
          contra: others.length === 1 ? nameOf(others[0].accountCode)
            : others.length > 1 ? `${others.length} accounts` : '—',
        });
      }
    }
    return out.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  }, [journals, bank, accounts]);

  let running = 0;
  const withBalance = rows.map((r) => {
    running += r.dr - r.cr;
    return { ...r, balance: Math.round(running * 100) / 100 };
  });

  const totalIn = rows.reduce((n, r) => n + r.dr, 0);
  const totalOut = rows.reduce((n, r) => n + r.cr, 0);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="emerald" icon={ArrowDownLeft} value={money(totalIn)} label="In" />
        <StatChip tone="rose" icon={ArrowUpRight} value={money(totalOut)} label="Out" />
        <StatChip tone="navy" value={money(totalIn - totalOut)} label="Closing balance" />
      </div>

      <SoftCard pad="p-0">
        <PanelHeader
          icon={BookOpen} tone="navy" title="The book"
          subtitle="Every movement through this account, whatever raised it"
          right={(
            <select value={bank} onChange={(e) => onBank(e.target.value)}
              className={glassFieldClass('max-w-[220px] text-xs')}>
              {banks.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
            </select>
          )}
        />

        {withBalance.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            Nothing has gone through this account yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Date</th>
                  <th className="px-2 font-semibold">Journal</th>
                  <th className="px-2 font-semibold">Description</th>
                  <th className="px-2 font-semibold">Other side</th>
                  <th className="px-2 font-semibold text-right">In</th>
                  <th className="px-2 font-semibold text-right">Out</th>
                  <th className="px-2 pr-4 font-semibold text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {withBalance.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 dark:border-gray-800">
                    <td className="py-2 pl-4 pr-2 tabular-nums text-gray-600 dark:text-gray-300">
                      {r.date}
                    </td>
                    <td className="px-2 font-mono text-xs text-gray-500">{r.ref}</td>
                    <td className="px-2 text-gray-800 dark:text-gray-100 truncate max-w-[260px]">
                      {r.narration}
                    </td>
                    <td className="px-2 text-gray-500 text-xs">{r.contra}</td>
                    <td className="px-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                      {r.dr ? money(r.dr) : ''}
                    </td>
                    <td className="px-2 text-right tabular-nums text-red-700 dark:text-red-400">
                      {r.cr ? money(r.cr) : ''}
                    </td>
                    <td className="px-2 pr-4 text-right tabular-nums font-medium">
                      {money(r.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>
    </>
  );
}
